"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import LemonMark from "@/components/ui/LemonMark";
import {
  Search,
  History,
  Workflow,
  CalendarClock,
  Activity,
  Users,
  FileText,
  Library,
  Plug,
  Crown,
  ShieldCheck,
  Pencil,
  MessageCircle,
  MessageCircleWarning,
  Inbox,
  Sun,
  PanelLeftClose,
  LayoutGrid,
  Settings,
  LogOut,
} from "lucide-react";

type IconProps = { size: number; style?: React.CSSProperties };

type TeamRole = "admin" | "manager" | "member";

type TeamMember = {
  name: string;
  email: string;
  initials: string;
  role: TeamRole;
};

const members: TeamMember[] = [
  {
    name: "David Kim",
    email: "david@launchlemonade.app",
    initials: "DK",
    role: "admin",
  },
  {
    name: "Sarah Lee",
    email: "sarah.contractor@launchlemonade.app",
    initials: "SL",
    role: "admin",
  },
  {
    name: "Mike Ramos",
    email: "mike@launchlemonade.app",
    initials: "MR",
    role: "manager",
  },
  {
    name: "Anna Reyes",
    email: "anna@launchlemonade.app",
    initials: "AR",
    role: "manager",
  },
  {
    name: "Tom Bailey",
    email: "tom@launchlemonade.app",
    initials: "TB",
    role: "member",
  },
];

const roleMeta: Record<
  TeamRole,
  { label: string; Icon: (props: IconProps) => React.ReactElement }
> = {
  admin: { label: "Team Admin", Icon: (p) => <Crown {...p} /> },
  manager: { label: "Team Manager", Icon: (p) => <ShieldCheck {...p} /> },
  member: { label: "Team Member", Icon: (p) => <Pencil {...p} /> },
};

const workspaceNav = [
  {
    label: "Assistants",
    Icon: (p: IconProps) => <LayoutGrid {...p} />,
    children: [
      { label: "Chat History", Icon: (p: IconProps) => <History {...p} /> },
    ],
  },
  {
    label: "Workflows",
    Icon: (p: IconProps) => <Workflow {...p} />,
    children: [
      { label: "Scheduled", Icon: (p: IconProps) => <CalendarClock {...p} /> },
      { label: "Run History", Icon: (p: IconProps) => <Activity {...p} /> },
    ],
  },
  {
    label: "Team",
    Icon: (p: IconProps) => <Users {...p} />,
    active: true,
    children: [],
  },
];

const setupNav = [
  { label: "Skills", Icon: (p: IconProps) => <FileText {...p} /> },
  { label: "Knowledge", Icon: (p: IconProps) => <Library {...p} /> },
  { label: "Integrations", Icon: (p: IconProps) => <Plug {...p} /> },
];

export default function TeamAppPreview({
  className = "",
  glass = false,
}: {
  className?: string;
  /** Renders the outer window frame as frosted glass, matching
      ChatAppPreview's `glass` recipe — needs a glow blob behind it. */
  glass?: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";

  const c = isDark
    ? {
        shadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
        chromeBg: "#0d0d0e",
        chromeBorder: "rgba(255,255,255,0.08)",
        glassBg: "rgba(13,13,14,0.55)",
        glassBorder: "rgba(255,255,255,0.1)",
        surfaceBg: "#0d0d0e",
        sidebarBg: "transparent",
        sidebarBorder: "rgba(255,255,255,0.08)",
        logoText: "rgba(255,255,255,0.9)",
        searchBg: "rgba(255,255,255,0.06)",
        searchBorder: "rgba(255,255,255,0.08)",
        searchText: "rgba(255,255,255,0.35)",
        kbdBg: "rgba(255,255,255,0.08)",
        kbdText: "rgba(255,255,255,0.4)",
        sectionLabel: "rgba(255,255,255,0.3)",
        navText: "rgba(255,255,255,0.55)",
        navIcon: "rgba(255,255,255,0.4)",
        navActiveBg: "rgba(255,255,255,0.08)",
        navActiveText: "rgba(255,255,255,0.92)",
        navLineColor: "rgba(255,255,255,0.12)",
        planCardBg: "#000000",
        planCardBorder: "rgba(255,255,255,0.14)",
        planText: "rgba(255,255,255,0.85)",
        planSubText: "rgba(255,255,255,0.4)",
        usageLabel: "rgba(255,255,255,0.3)",
        usageTrack: "rgba(255,255,255,0.1)",
        usageFill: "#e0a730",
        avatarBg: "rgba(255,255,255,0.1)",
        avatarText: "rgba(255,255,255,0.75)",
        badgeBg: "#c2452f",
        badgeText: "#ffffff",
        headingText: "rgba(255,255,255,0.95)",
        subText: "rgba(255,255,255,0.45)",
        rowBg: "#1b1b1b",
        rowBorder: "rgba(255,255,255,0.07)",
        memberAvatarBg: "rgba(255,255,255,0.08)",
        memberAvatarText: "#e0a730",
        memberName: "rgba(255,255,255,0.92)",
        memberEmail: "rgba(255,255,255,0.42)",
        adminBg: "#FFE500",
        adminText: "#161616",
        roleBorder: "rgba(255,255,255,0.14)",
        roleText: "rgba(255,255,255,0.6)",
        footerIcon: "rgba(255,255,255,0.4)",
        footerText: "rgba(255,255,255,0.5)",
      }
    : {
        shadow: "0 30px 80px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.02)",
        chromeBg: "#f1efe9",
        chromeBorder: "rgba(0,0,0,0.06)",
        glassBg: "rgba(255,255,255,0.55)",
        glassBorder: "rgba(255,255,255,0.7)",
        surfaceBg: "#E3E3DF",
        sidebarBg: "#F4F4F2",
        sidebarBorder: "rgba(0,0,0,0.06)",
        logoText: "rgba(0,0,0,0.85)",
        searchBg: "rgba(255,255,255,0.7)",
        searchBorder: "rgba(0,0,0,0.08)",
        searchText: "rgba(0,0,0,0.35)",
        kbdBg: "rgba(0,0,0,0.05)",
        kbdText: "rgba(0,0,0,0.4)",
        sectionLabel: "rgba(0,0,0,0.32)",
        navText: "rgba(0,0,0,0.6)",
        navIcon: "rgba(0,0,0,0.45)",
        navActiveBg: "rgba(0,0,0,0.07)",
        navActiveText: "rgba(0,0,0,0.88)",
        navLineColor: "rgba(0,0,0,0.1)",
        planCardBg: "rgba(255,255,255,0.6)",
        planCardBorder: "rgba(0,0,0,0.08)",
        planText: "rgba(0,0,0,0.82)",
        planSubText: "rgba(0,0,0,0.4)",
        usageLabel: "rgba(0,0,0,0.32)",
        usageTrack: "rgba(0,0,0,0.08)",
        usageFill: "#c9820a",
        avatarBg: "rgba(0,0,0,0.08)",
        avatarText: "rgba(0,0,0,0.7)",
        badgeBg: "#c2452f",
        badgeText: "#ffffff",
        headingText: "rgba(0,0,0,0.88)",
        subText: "rgba(0,0,0,0.45)",
        rowBg: "#ffffff",
        rowBorder: "rgba(0,0,0,0.07)",
        memberAvatarBg: "rgba(0,0,0,0.05)",
        memberAvatarText: "#c9820a",
        memberName: "rgba(0,0,0,0.85)",
        memberEmail: "rgba(0,0,0,0.4)",
        adminBg: "#FFE500",
        adminText: "#161616",
        roleBorder: "rgba(0,0,0,0.14)",
        roleText: "rgba(0,0,0,0.55)",
        footerIcon: "rgba(0,0,0,0.4)",
        footerText: "rgba(0,0,0,0.55)",
      };

  const glassActive = glass;

  return (
    <div
      className={`relative select-none text-left ${className}`}
      aria-hidden="true"
    >
      <div
        className={`flex aspect-[3/4] md:aspect-[2980/1894] overflow-hidden ${glassActive ? "hero-glass-surface" : "rounded-2xl"}`}
        style={
          glassActive
            ? undefined
            : {
                background: c.glassBg,
                borderTop: `1px solid ${c.glassBorder}`,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }
        }
      >
        {/* Sidebar */}
        <div
          className="hidden md:flex w-[210px] shrink-0 flex-col px-4 pt-4 pb-4"
          style={{
            background: c.sidebarBg,
            borderRight: `1px solid ${c.sidebarBorder}`,
          }}
        >
          <div className="flex items-center gap-2 px-1">
            <LemonMark size={18} />
            <span
              className="text-[14px] font-medium"
              style={{ color: c.logoText }}
            >
              LaunchLemonade
            </span>
          </div>

          <div
            className="mt-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2"
            style={{
              background: c.searchBg,
              border: `1px solid ${c.searchBorder}`,
            }}
          >
            <span className="flex items-center gap-2">
              <Search size={13} style={{ color: c.searchText }} />
              <span className="text-[12px]" style={{ color: c.searchText }}>
                Search...
              </span>
            </span>
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{ background: c.kbdBg, color: c.kbdText }}
            >
              ⌘K
            </span>
          </div>

          <p
            className="mt-3 px-1 text-[10px] font-medium tracking-wide"
            style={{ color: c.sectionLabel }}
          >
            WORKSPACE
          </p>
          <div className="mt-1 flex flex-col gap-0.5">
            {workspaceNav.map((item) => (
              <div key={item.label}>
                <div
                  className="flex items-center gap-2 rounded-lg px-2 py-1"
                  style={{
                    background: item.active ? c.navActiveBg : undefined,
                  }}
                >
                  <item.Icon
                    size={13}
                    style={{ color: item.active ? c.navActiveText : c.navIcon }}
                  />
                  <span
                    className="text-[12px]"
                    style={{ color: item.active ? c.navActiveText : c.navText }}
                  >
                    {item.label}
                  </span>
                </div>
                {item.children.length > 0 && (
                  <div className="relative mt-0.5 flex flex-col gap-0.5">
                    <span
                      aria-hidden="true"
                      className="absolute w-px"
                      style={{
                        left: 14.5,
                        top: 0,
                        bottom: 12,
                        background: c.navLineColor,
                      }}
                    />
                    {item.children.map((child) => (
                      <div
                        key={child.label}
                        className="flex items-center gap-2 rounded-lg px-2 py-1"
                        style={{ marginLeft: 14 }}
                      >
                        <child.Icon size={13} style={{ color: c.navIcon }} />
                        <span
                          className="text-[12px]"
                          style={{ color: c.navText }}
                        >
                          {child.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p
            className="mt-3 px-1 text-[10px] font-medium tracking-wide"
            style={{ color: c.sectionLabel }}
          >
            SET UP
          </p>
          <div className="mt-1 flex flex-col gap-0.5">
            {setupNav.map(({ label, Icon }) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5"
              >
                <Icon size={13} style={{ color: c.navIcon }} />
                <span className="text-[12px]" style={{ color: c.navText }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          <div className="flex-1" />

          <div
            className="-mx-4 -mb-4 rounded-xl px-3 py-3 flex flex-col gap-3 border"
            style={{ background: c.planCardBg, borderColor: c.planCardBorder }}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                style={{ background: c.avatarBg, color: c.avatarText }}
              >
                J
              </span>
              <span className="flex flex-1 min-w-0 flex-col leading-tight">
                <span
                  className="truncate text-[11px] font-medium"
                  style={{ color: c.planText }}
                >
                  John Doe
                </span>
                <span className="text-[9px]" style={{ color: c.planSubText }}>
                  Pro plan
                </span>
              </span>
              <Settings size={13} style={{ color: c.footerIcon }} />
              <LogOut size={13} style={{ color: c.footerIcon }} />
            </div>

            <div>
              <p
                className="text-[9px] font-medium tracking-wide"
                style={{ color: c.usageLabel }}
              >
                FREE USAGE
              </p>
              <div
                className="mt-2 h-1.5 w-full rounded-full"
                style={{ background: c.usageTrack }}
              >
                <div
                  className="h-1.5 w-[8%] rounded-full"
                  style={{ background: c.usageFill }}
                />
              </div>
              <div
                className="mt-2.5 rounded-md py-1.5 text-center text-[11px] font-medium"
                style={{ background: "#FFE500", color: "#161616" }}
              >
                Upgrade to Pro
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <MessageCircleWarning size={13} style={{ color: c.footerIcon }} />
              <span className="text-[11px]" style={{ color: c.footerText }}>
                Report a problem
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <Inbox size={13} style={{ color: c.footerIcon }} />
              <span className="text-[11px]" style={{ color: c.footerText }}>
                Zestie Inbox
              </span>
              <span
                className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-medium"
                style={{ background: c.badgeBg, color: c.badgeText }}
              >
                2
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Sun size={13} style={{ color: c.footerIcon }} />
                <span className="text-[11px]" style={{ color: c.footerText }}>
                  Theme
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <PanelLeftClose size={13} style={{ color: c.footerIcon }} />
                <span className="text-[11px]" style={{ color: c.footerText }}>
                  Collapse
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div
          className="flex-1 min-w-0 flex flex-col px-6 py-6 md:px-9 md:py-9 overflow-hidden"
          style={{ background: c.surfaceBg }}
        >
          <div>
            <p
              className="text-[18px] md:text-[22px] font-medium"
              style={{ color: c.headingText }}
            >
              Team Lemonade
            </p>
            <p
              className="mt-1.5 text-[11px] md:text-[12px]"
              style={{ color: c.subText }}
            >
              {members.length} members
            </p>
          </div>

          <p
            className="mt-6 text-[10px] font-medium tracking-wide"
            style={{ color: c.sectionLabel }}
          >
            MEMBERS
          </p>

          <div className="mt-3 flex flex-1 flex-col gap-3 overflow-hidden">
            {members.map((member) => {
              const { label, Icon } = roleMeta[member.role];
              const isAdmin = member.role === "admin";
              return (
                <div
                  key={member.email}
                  className="flex items-center gap-3 rounded-xl border px-4 py-3.5"
                  style={{ background: c.rowBg, borderColor: c.rowBorder }}
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[11px] font-medium"
                    style={{
                      background: c.memberAvatarBg,
                      color: c.memberAvatarText,
                    }}
                  >
                    {member.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p
                      className="truncate text-[12.5px] font-medium"
                      style={{ color: c.memberName }}
                    >
                      {member.name}
                    </p>
                    <p
                      className="mt-0.5 truncate text-[11px]"
                      style={{ color: c.memberEmail }}
                    >
                      {member.email}
                    </p>
                  </div>
                  <span
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium"
                    style={
                      isAdmin
                        ? { background: c.adminBg, color: c.adminText }
                        : {
                            border: `1px solid ${c.roleBorder}`,
                            color: c.roleText,
                          }
                    }
                  >
                    <Icon
                      size={12}
                      style={
                        isAdmin ? { color: c.adminText } : { color: c.roleText }
                      }
                    />
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Floating chat bubble — inset so it reads as part of the dashboard, not spilling past its frame */}
      <span
        className="absolute bottom-5 right-5 hidden md:flex h-11 w-11 items-center justify-center rounded-full"
        style={{ background: "#FFE500", boxShadow: c.shadow }}
      >
        <MessageCircle
          size={19}
          strokeWidth={2.25}
          style={{ color: "#161616" }}
        />
      </span>
    </div>
  );
}
